/**
 * Importer un tableau (03/11/2026): a table pasted from a spreadsheet or read
 * from a CSV file, and what each column is. The role of a column is only
 * proposed from its header, as lemlist's import does; the person confirms or
 * changes it before anything is written.
 */

import { nameWords } from './app-object-keys.ts';

export type ImportRole =
  | 'company' | 'domain' | 'first_name' | 'last_name' | 'full_name' | 'job_title' | 'email' | 'phone'
  | 'parameter' | 'ignore';

export const IMPORT_ROLE_LABELS: Record<ImportRole, string> = {
  company: 'Entreprise',
  domain: 'Site',
  first_name: 'Prénom',
  last_name: 'Nom',
  full_name: 'Nom complet',
  job_title: 'Poste',
  email: 'E-mail',
  phone: 'Téléphone',
  parameter: 'Paramètre',
  ignore: 'Ignorer',
};

/** Roles a table may hold once; « parameter » and « ignore » any number of times. */
export const SINGLE_ROLES: ReadonlySet<ImportRole> = new Set(['company', 'domain', 'first_name', 'last_name', 'full_name', 'job_title', 'email', 'phone']);

export interface ImportColumn {
  header: string;
  as: ImportRole;
}

/** The separator a pasted or exported table uses: tab (spreadsheet), semicolon (French CSV), else comma. */
const separatorOf = (firstLine: string): string =>
  firstLine.includes('\t') ? '\t' : (firstLine.split(';').length > firstLine.split(',').length ? ';' : ',');

/**
 * The table inside a message (« Prépare la prospection sur ce tableau : » then
 * the rows): the longest run of lines that split into the same number of
 * cells, two at least, with the separator most lines use.
 */
export const tableBlock = (text: string): string => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const separator = ['\t', ';', ','].map((candidate) => ({ candidate, lines: lines.filter((line) => line.includes(candidate)).length }))
    .sort((left, right) => right.lines - left.lines)[0]!.candidate;
  const width = (line: string) => line.trim() ? line.split(separator).length : 0;
  let best: { start: number; end: number } | null = null;
  for (let start = 0; start < lines.length; start += 1) {
    const cells = width(lines[start]!);
    if (cells < 2) continue;
    let end = start;
    while (end + 1 < lines.length && width(lines[end + 1]!) === cells) end += 1;
    if (!best || end - start > best.end - best.start) best = { start, end };
    start = end;
  }
  return best ? lines.slice(best.start, best.end + 1).join('\n') : text;
};

/** Rows of cells, quoted fields and line breaks inside quotes included; empty rows dropped. */
export const parseTable = (text: string): string[][] => {
  const source = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const separator = separatorOf(source.split('\n')[0] ?? '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index]!;
    if (quoted) {
      if (char === '"' && source[index + 1] === '"') { cell += '"'; index += 1; }
      else if (char === '"') quoted = false;
      else cell += char;
    } else if (char === '"' && cell === '') quoted = true;
    else if (char === separator) { row.push(cell.trim()); cell = ''; }
    else if (char === '\n') { row.push(cell.trim()); rows.push(row); row = []; cell = ''; }
    else cell += char;
  }
  row.push(cell.trim());
  rows.push(row);
  return rows.filter((cells) => cells.some((value) => value !== ''));
};

const GUESSES: Array<{ role: ImportRole; words: string[][] }> = [
  { role: 'email', words: [['email'], ['e', 'mail'], ['mail'], ['courriel'], ['adresse', 'mail'], ['adresse', 'e', 'mail']] },
  { role: 'first_name', words: [['prenom'], ['first', 'name'], ['firstname'], ['given', 'name']] },
  { role: 'last_name', words: [['nom', 'de', 'famille'], ['last', 'name'], ['lastname'], ['surname'], ['nom']] },
  { role: 'full_name', words: [['nom', 'complet'], ['full', 'name'], ['fullname'], ['contact'], ['name'], ['personne']] },
  { role: 'job_title', words: [['poste'], ['fonction'], ['titre'], ['job', 'title'], ['jobtitle'], ['title'], ['role']] },
  { role: 'phone', words: [['telephone'], ['tel'], ['phone'], ['mobile'], ['portable']] },
  { role: 'domain', words: [['site'], ['site', 'web'], ['website'], ['domaine'], ['domain'], ['url']] },
  { role: 'company', words: [['entreprise'], ['societe'], ['company'], ['company', 'name'], ['organisation'], ['organization'], ['compte'], ['account'], ['raison', 'sociale']] },
];

/** The role a header suggests, the first match winning; a column used twice falls back to a parameter. */
export const guessColumns = (headers: string[]): ImportColumn[] => {
  const used = new Set<ImportRole>();
  return headers.map((header) => {
    const words = nameWords(header).join(' ');
    const guess = GUESSES.find((item) => !used.has(item.role) && item.words.some((option) => option.join(' ') === words))?.role ?? 'parameter';
    if (SINGLE_ROLES.has(guess)) used.add(guess);
    return { header, as: guess };
  });
};

/** What a mapping lacks to make objects: a company column (the list is of companies). */
export const importProblem = (columns: ImportColumn[]): string | null => {
  if (!columns.some((column) => column.as === 'company')) return 'Choisissez la colonne Entreprise.';
  const repeated = [...SINGLE_ROLES].find((role) => columns.filter((column) => column.as === role).length > 1);
  return repeated ? `« ${IMPORT_ROLE_LABELS[repeated]} » est choisi pour deux colonnes.` : null;
};
